// Single fallback image path
const fallbackImage = '/fallback.png'

export function getFallbackImage (cuisine = '') {
  // Cuisine parameter is kept for backward compatibility
  // but is no longer used since we have only one fallback image
  return fallbackImage
}

export function handleAvatarError (event) {
  event.target.onerror = null
  event.target.src = '/avatar_placeholder.png'
}

/**
 * Handle failed recipe image loads: stop retries from <picture>/<source> and show fallback once.
 */
export function handleRecipeImageError (event, cuisine = '') {
  const img = event?.target
  if (!img || img.dataset.imageFallbackApplied === '1') {
    return
  }

  img.dataset.imageFallbackApplied = '1'
  img.onerror = null

  const pictureEl = img.closest('picture')
  if (pictureEl) {
    pictureEl.querySelectorAll('source').forEach((source) => source.remove())
  }

  img.src = getFallbackImage(cuisine)
}

/**
 * Get the appropriate recipe image URL with responsive sizing
 *
 * @param {string} imageUrl - The image URL or base filename
 * @param {string} cuisine - The cuisine (for fallback)
 * @param {number} size - Desired image size (480, 768, 1024, 1600, or 0 for original)
 * @param {string} imageBaseName - The base filename without extension (new format)
 * @returns {string} - The complete image URL
 */
export function getRecipeImage (imageUrl, cuisine = '', size = 0, imageBaseName = null) {
  // If imageBaseName is provided, use it (new format)
  if (imageBaseName) {
    return getImageFromBaseName(imageBaseName, size)
  }

  // If no image URL is provided, return a fallback image
  if (!imageUrl) {
    return getFallbackImage(cuisine)
  }

  // Handle case where imageUrl is already a full URL
  if (imageUrl.startsWith('http')) {
    return imageUrl
  }

  // Handle case where imageUrl is a path with extension (old format)
  if (imageUrl.includes('.')) {
    if (imageUrl.startsWith('/storage')) {
      const imageOrigin = getSiteUrlForImages()
      return imageOrigin ? `${imageOrigin}${imageUrl}` : imageUrl
    }
    return imageUrl
  }

  // New format: imageUrl is just the base filename without extension
  return getImageFromBaseName(imageUrl, size)
}

function getSiteUrlForImages () {
  const isCapacitor = window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yumnia.com'
  const storageBaseUrl = import.meta.env.VITE_STORAGE_BASE_URL || siteUrl

  if (import.meta.env.DEV || import.meta.env.VITE_ENV === 'development') {
    return storageBaseUrl
  }

  return isCapacitor ? siteUrl : ''
}

/**
 * Get image URL from base name with responsive sizing
 *
 * @param {string} baseName - The base filename without extension
 * @param {number} size - Desired image size (480, 768, 1024, 1600, or 0 for original)
 * @returns {string} - The complete image URL
 */
function getImageFromBaseName (baseName, size = 0) {
  // Determine if we're in a Capacitor native app
  const siteUrl = getSiteUrlForImages()

  // Determine the appropriate size based on the size parameter
  const validSizes = [360, 480, 768, 1024, 1600]
  let useSize = 0

  if (size > 0) {
    // Find the closest size that is larger than or equal to the requested size
    for (const validSize of validSizes) {
      if (validSize >= size) {
        useSize = validSize
        break
      }
    }
    // If no valid size is found, use the largest available
    if (useSize === 0) {
      useSize = validSizes[validSizes.length - 1]
    }
  }

  // Construct the image URL based on the size
  let imagePath
  if (useSize > 0) {
    imagePath = `/storage/recipes/resized/${baseName}-${useSize}px.webp`
  } else {
    imagePath = `/storage/recipes/${baseName}.webp`
  }

  // Add site URL for Capacitor
  return `${siteUrl}${imagePath}`
}

/**
 * Generate srcset attribute for responsive images
 *
 * @param {string} imageBaseName - The base filename without extension
 * @returns {string} - The srcset attribute value
 */
export function getImageSrcSet (imageBaseName) {
  if (!imageBaseName) {
    return ''
  }

  const siteUrl = getSiteUrlForImages()
  const sizes = [1600, 1024, 768, 480, 360]

  return sizes.map(size => {
    let path = `${siteUrl}/storage/recipes/resized/${imageBaseName}-${size}px.webp`
    return `${path} ${size}w`
  }).join(', ')
}

/**
 * Resolve a backend "/storage/..." path (e.g. user-uploaded community photos)
 * to a fully qualified URL when needed (Capacitor native and dev), otherwise
 * return it unchanged for same-origin web. Uses the same storage-base origin as
 * recipe images (VITE_STORAGE_BASE_URL, falling back to VITE_SITE_URL).
 *
 * @param {string} path - A path such as "/storage/recipe-photos/12/uuid.webp"
 * @returns {string}
 */
export function resolveStorageUrl (path) {
  if (!path) {
    return ''
  }
  if (path.startsWith('http')) {
    return path
  }
  const siteUrl = getSiteUrlForImages()
  return `${siteUrl}${path}`
}

export function preloadFallbackImages () {
  // Preload only the single fallback image
  const img = new Image()
  img.src = fallbackImage
}
